import type { Task } from '@crossmatch/shared';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { tasksApi } from '../api/tasks.js';
import { Button } from './Button.js';
import { Tag } from './Tag.js';
import { formatDue } from '../lib/format.js';
import {
  NEXT_TASK_STATUS,
  PHASE_LABELS,
  PHASE_ORDER,
  TASK_STATUS_LABELS,
  TASK_STATUS_ORDER,
  taskPhaseLabel,
} from '../lib/labels.js';
import { qk } from '../lib/queryKeys.js';

/** 逾期判定只做展示染色（红色徽标），不涉及任何分数计算。 */
function isOverdue(dueDate: string | null | undefined): boolean {
  if (dueDate === null || dueDate === undefined) return false;
  const ms = Date.parse(dueDate);
  return !Number.isNaN(ms) && ms < Date.now();
}

/** 阶段条：4 个阶段的完成度（done 数 / 总条数），成队项目一眼看出整体进度。 */
export function PhaseBar({ tasks }: { tasks: Task[] }) {
  return (
    <div className="phase-bar" aria-label="阶段进度">
      {PHASE_ORDER.map((phase) => {
        const phaseTasks = tasks.filter((task) => task.phase === phase);
        const done = phaseTasks.filter((task) => task.status === 'done').length;
        const ratio = phaseTasks.length === 0 ? 0 : done / phaseTasks.length;
        return (
          <div key={phase} className="phase-bar-item">
            <div className="row-tight">
              <span className="small">{PHASE_LABELS[phase] ?? `阶段 ${phase}`}</span>
              <span className="muted small">
                {done}/{phaseTasks.length}
              </span>
            </div>
            <div
              className="phase-bar-track"
              role="progressbar"
              aria-valuenow={Math.round(ratio * 100)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label={`${PHASE_LABELS[phase] ?? `阶段 ${phase}`}完成度`}
            >
              <div className="phase-bar-fill" style={{ width: `${Math.round(ratio * 100)}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
}

export interface KanbanMember {
  userId: string;
  name: string;
}

/**
 * 队内看板（T70）：三列（待办/进行中/已完成），「移到下一列」按钮流转（与 Tasks 页同一状态机），
 * due 徽标逾期红色，队长可指派负责人（下拉选已确认成员）；改期对所有成员开放（T69 语义）。
 * 状态推进/指派的写操作走 PATCH /tasks/:id，失败由全局 MutationCache 弹 Toast。
 */
export function Kanban({
  tasks,
  projectId,
  members,
  canAssign,
}: {
  tasks: Task[];
  projectId: string;
  members: KanbanMember[];
  canAssign: boolean;
}) {
  const queryClient = useQueryClient();
  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: qk.projectTasks(projectId) });
    void queryClient.invalidateQueries({ queryKey: qk.myTasks() });
  };

  const moveTask = useMutation({
    mutationFn: (input: { task: Task; status: Task['status'] }) => tasksApi.update(input.task.id, { status: input.status }),
    onSuccess: invalidate,
  });
  const assignTask = useMutation({
    mutationFn: (input: { task: Task; ownerId: string | null }) => tasksApi.assign(input.task.id, input.ownerId),
    onSuccess: invalidate,
  });
  const setDue = useMutation({
    mutationFn: (input: { task: Task; dueDate: string | null }) => tasksApi.setDue(input.task.id, input.dueDate),
    onSuccess: invalidate,
  });

  return (
    <div className="kanban">
      {TASK_STATUS_ORDER.map((status) => {
        const columnTasks = tasks.filter((task) => task.status === status);
        return (
          <section className="kanban-col" key={status} aria-label={`${TASK_STATUS_LABELS[status]}列`}>
            <header className="kanban-col-head">
              <Tag tone={status === 'done' ? 'ok' : status === 'doing' ? 'primary' : 'muted'}>
                {TASK_STATUS_LABELS[status]}
              </Tag>
              <span className="kanban-count">{columnTasks.length} 条</span>
            </header>

            {columnTasks.length === 0 ? (
              <p className="muted small">本列暂时没有任务。</p>
            ) : (
              columnTasks.map((task) => {
                const nextStatus = NEXT_TASK_STATUS[task.status];
                const pending =
                  (moveTask.isPending && moveTask.variables?.task.id === task.id) ||
                  (assignTask.isPending && assignTask.variables?.task.id === task.id) ||
                  (setDue.isPending && setDue.variables?.task.id === task.id);
                const overdue = isOverdue(task.dueDate) && task.status !== 'done';
                return (
                  <article className="card tight task-card" key={task.id} aria-label={task.title}>
                    <div className="row-tight">
                      <strong className="grow">{task.title}</strong>
                      <Tag tone="primary">{taskPhaseLabel(task.phase)}</Tag>
                    </div>
                    <span className={overdue ? 'small due-overdue' : 'muted small'}>
                      {overdue ? '⚠ 已逾期：' : ''}
                      {formatDue(task.dueDate)}
                    </span>
                    <div className="row-tight">
                      <label className="small muted" htmlFor={`kanban-owner-${task.id}`}>
                        负责人
                      </label>
                      <select
                        id={`kanban-owner-${task.id}`}
                        className="select"
                        value={task.ownerId ?? ''}
                        disabled={!canAssign || pending}
                        title={canAssign ? undefined : '仅队长可指派（T69）'}
                        onChange={(event) =>
                          assignTask.mutate({ task, ownerId: event.target.value === '' ? null : event.target.value })
                        }
                      >
                        <option value="">未指派</option>
                        {members.map((member) => (
                          <option key={member.userId} value={member.userId}>
                            {member.name}
                          </option>
                        ))}
                      </select>
                      <input
                        className="input kanban-due"
                        type="date"
                        aria-label="截止日期"
                        value={task.dueDate ? task.dueDate.slice(0, 10) : ''}
                        disabled={pending}
                        onChange={(event) =>
                          setDue.mutate({ task, dueDate: event.target.value === '' ? null : event.target.value })
                        }
                      />
                    </div>
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={moveTask.isPending && moveTask.variables?.task.id === task.id}
                      onClick={() => moveTask.mutate({ task, status: nextStatus })}
                    >
                      移到「{TASK_STATUS_LABELS[nextStatus]}」
                    </Button>
                  </article>
                );
              })
            )}
          </section>
        );
      })}
    </div>
  );
}
